// Pakistani CNIC entry: 13 digits shown as 42401-1234567-8.
//
// The field reformats on every keystroke. Formatting alone left the caret at the end after each
// one, so correcting a digit in the middle sent the next digit to the end: fixing 42401-1234567-8
// produced 42401-1235678-9, a different CNIC that still passes validation. A Backspace just after a
// hyphen removed only the hyphen, which formatting then put straight back, so it seemed to do
// nothing.

/** Groups the digits 5-7-1 and drops anything past the thirteenth. */
export const formatCnic = (value) => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 13);
  if (digits.length <= 5) return digits;
  if (digits.length <= 12) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  return `${digits.slice(0, 5)}-${digits.slice(5, 12)}-${digits.slice(12)}`;
};

/**
 * Applies one edit to a CNIC field and works out where the caret belongs afterwards.
 *
 * @param {string} previous   the formatted value before the edit
 * @param {string} rawValue   the input's value as the browser left it after the edit
 * @param {number} rawCaret   the input's selectionStart after the edit
 * @param {string} [inputType] the InputEvent's inputType, e.g. 'deleteContentBackward'
 * @returns {{ value: string, caret: number }}
 */
export const editCnic = (previous, rawValue, rawCaret, inputType) => {
  const prev = String(previous || '');
  const raw = String(rawValue || '');
  const prevDigits = prev.replace(/\D/g, '');
  let digits = raw.replace(/\D/g, '');
  let before = raw.slice(0, rawCaret == null ? raw.length : rawCaret).replace(/\D/g, '').length;

  // Deleting a hyphen removes no digit. The digit beside it is what the user meant to delete.
  if (digits === prevDigits && raw.length < prev.length) {
    if (inputType === 'deleteContentBackward' && before > 0) {
      digits = digits.slice(0, before - 1) + digits.slice(before);
      before -= 1;
    } else if (inputType === 'deleteContentForward' && before < digits.length) {
      digits = digits.slice(0, before) + digits.slice(before + 1);
    }
  }

  digits = digits.slice(0, 13);
  before = Math.min(before, digits.length);
  const value = formatCnic(digits);

  // The caret goes back after the same number of digits it followed, whatever hyphens moved.
  let caret = 0;
  for (let seen = 0; caret < value.length && seen < before; caret += 1) {
    if (value[caret] !== '-') seen += 1;
  }
  return { value, caret };
};

/**
 * The onChange body for a CNIC input: formats the value, hands it to `setValue`, and puts the caret
 * back where the user was typing.
 *
 * @param {Event}    e         the React change event
 * @param {string}   previous  the field's current (formatted) value
 * @param {(value: string) => void} setValue
 */
export const handleCnicInput = (e, previous, setValue) => {
  const input = e.target;
  const { value, caret } = editCnic(previous, input.value, input.selectionStart, e.nativeEvent && e.nativeEvent.inputType);
  setValue(value);
  // React writes the formatted value back into the input after this handler, which moves the caret
  // to the end, so it is restored once that has happened.
  requestAnimationFrame(() => {
    if (document.activeElement === input) input.setSelectionRange(caret, caret);
  });
};
